import merge from 'lodash.merge';
import { getEnvironment } from './utils/googleAnalytics/getEnvironment';
import { getExperimentIds } from './utils/googleAnalytics/getExperimentIds';
import { logEvent } from './utils/googleAnalytics/logEvent';
import isSSR from '../utils/isSSR';
import getLogger from '../utils/getLogger';
export const clean = obj => {
  if (Array.isArray(obj)) {
    return obj.map(entry => clean(entry));
  }
  if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      if (obj[key] !== undefined && obj[key] !== null) {
        return {
          ...acc,
          [key]: clean(obj[key])
        };
      }
      return acc;
    }, {});
  }
  return obj;
};
export const DefaultVehicle = {
  catalog: null,
  chromeStyleId: null,
  vehicleId: null,
  listingId: null,
  year: null,
  makeId: null,
  modelId: null,
  seriesId: null,
  trimId: null,
  trimName: null,
  type: null,
  bodyStyle: null,
  subCategory: null,
  condition: null,
  mileage: null,
  fuelType: null,
  fuelEconomy: null,
  price: null,
  sellerType: null,
  productConfiguration: null,
  clickType: null,
  description: null,
  priorityType: null,
  customerType: null,
  category: null,
  driveTrain: null,
  engineType: null,
  makeName: null,
  marketModel: null,
  mid: null,
  modelName: null,
  series: null,
  videoId: null,
  vin: null,
  dealerPriorityType: null,
  dealershipId: null,
  exteriorColor: null,
  transmission: null
};
const click = (currentWindow, _dataLayer, {
  props,
  inherit = false
} = {}) => {
  currentWindow.queueddataLayerInteractions.push(logEvent(merge({
    event: 'analytics_event',
    eventElement: '',
    eventAction: 'click',
    eventResult: '',
    eventNonInteractive: false,
    ...(inherit ? {} : {
      eventData: {
        vehicle: {
          ...DefaultVehicle
        }
      }
    })
  }, props)));
};
const impression = (currentWindow, _dataLayer, {
  props,
  inherit = false
} = {}) => {
  currentWindow.queueddataLayerInteractions.push(logEvent(merge({
    event: 'analytics_event',
    eventElement: '',
    eventAction: 'impression',
    eventResult: '',
    eventNonInteractive: false,
    ...(inherit ? {} : {
      eventData: {
        vehicle: {
          ...DefaultVehicle
        }
      }
    })
  }, props)));
};
export const getUserData = dataLayer => {
  return {
    isLoggedIn: dataLayer?.mykbb?.isLoggedIn ? '1' : '0',
    isInternalUser: 'false',
    adBlockerState: 'off',
    zipCode: dataLayer?.location?.zip
  };
};
export const getPageData = dataLayer => {
  const page = dataLayer?.googleanalytics?.page || dataLayer?.googleanalytics?.common?.page;
  return {
    siteSection: page?.siteSection,
    pageType: page?.pageType,
    detailPageName: page?.detailPageName,
    experimentId: getExperimentIds(dataLayer?.testids),
    pixAllPXAId: dataLayer?.pixallcookies?.pxaid,
    pixAllABC: dataLayer?.pixallcookies?.abc,
    pixallPageInstanceId: dataLayer?.pageinstance,
    ...page
  };
};
export const getVehicleCondition = vehicleclass => {
  switch (vehicleclass?.toLowerCase()) {
    case 'newcar':
      return 'new';
    case 'usedcar':
      return 'used';
  }
  return vehicleclass;
};
const pageView = function (currentWindow, dataLayer, {
  props
} = {}) {
  if (!dataLayer?.googleanalytics?.common?.page?.pageType) {
    getLogger()?.logError({
      error: {
        name: 'DataLayerEventError',
        message: '"pageType" missing when populating dataLayer array.',
        isSSR: isSSR()
      },
      googleAnalytics: JSON.stringify(dataLayer?.googleanalytics)
    });
  }
  currentWindow.queueddataLayerInteractions.push(logEvent(clean(merge({
    event: 'page_data',
    common: {
      dataLayerVersion: '1',
      application: {
        businessUnit: 'kelley blue book',
        name: 'web',
        version: `kbb ${(undefined || '').trim()}`,
        environment: getEnvironment(dataLayer?.diagnostics?.environment)
      },
      user: getUserData(dataLayer),
      page: getPageData(dataLayer)
    },
    vehicle: {
      catalog: dataLayer?.info?.catalog?.toString() || 'kbb',
      chromeStyleId: dataLayer?.info?.chromeStyleIds?.[0]?.toString(),
      vehicleId: dataLayer?.info?.vehicle?.id?.toString(),
      year: dataLayer?.info?.yearid?.toString(),
      makeId: dataLayer?.info?.catalog?.toString() === 'cads' && dataLayer?.info?.manufacturer?.name || dataLayer?.info?.manufacturer?.id?.toString(),
      modelId: dataLayer?.info?.catalog?.toString() === 'cads' && dataLayer?.info?.model?.name || dataLayer?.info?.model?.id?.toString(),
      trimId: dataLayer?.info?.vehicle?.trimid?.toString(),
      trimName: dataLayer?.info?.vehicle?.name,
      type: dataLayer?.info?.vehicle?.genericbodystyle,
      bodyStyle: dataLayer?.info?.bodyStyle,
      category: dataLayer?.info?.categories?.ad?.name,
      subCategory: dataLayer?.info?.categories?.sub?.code,
      condition: getVehicleCondition(dataLayer?.info?.vehicle?.vehicleclass),
      mileage: dataLayer?.info?.mileage?.toString(),
      fuelType: dataLayer?.info?.vehicle?.enginetype?.toString(),
      fuelEconomy: dataLayer?.info?.vehicle?.epacombined?.toString(),
      driveTrain: dataLayer?.info?.vehicle?.drivetrain,
      makeName: dataLayer?.info?.manufacturer?.name,
      marketModel: dataLayer?.info?.model?.marketname,
      modelName: dataLayer?.info?.model?.name
    },
    consumerInsights: {
      apiTrackingId: dataLayer?.peIds?.ids?.peTrackingId,
      fixedOps: dataLayer?.consumerinsightsdata?.insights?.fixedOps,
      hasTradeInLikelihood: dataLayer?.consumerinsightsdata?.insights?.hasTradeInLikelihood,
      lastActivityDate: dataLayer?.consumerinsightsdata?.insights?.lastActivityDate,
      makeOfPreferredModel: dataLayer?.consumerinsightsdata?.insights?.makeOfPreferredModel,
      makeOfRecentActivityPreferredModel: dataLayer?.consumerinsightsdata?.insights?.makeOfRecentActivityPreferredModel,
      makeOfSecondPreferredModel: dataLayer?.consumerinsightsdata?.insights?.makeOfSecondPreferredModel,
      marketLevel: dataLayer?.consumerinsightsdata?.insights?.marketLevel,
      predictedTradeInConfidence: dataLayer?.consumerinsightsdata?.insights?.predictedTradeInConfidence,
      predictedTradeInMake: dataLayer?.consumerinsightsdata?.insights?.predictedTradeInMake,
      predictedTradeInModel: dataLayer?.consumerinsightsdata?.insights?.predictedTradeInModel,
      predictedTradeInYear: dataLayer?.consumerinsightsdata?.insights?.predictedTradeInYear,
      preferredBodystyle: dataLayer?.consumerinsightsdata?.insights?.preferredBodystyle,
      preferredChromeStyleID: dataLayer?.consumerinsightsdata?.insights?.preferredChromeStyleID,
      preferredChromeStyleIDConfidence: dataLayer?.consumerinsightsdata?.insights?.preferredChromeStyleIDConfidence,
      preferredFuelCategory: dataLayer?.consumerinsightsdata?.insights?.preferredFuelCategory,
      preferredMake: dataLayer?.consumerinsightsdata?.insights?.preferredMake,
      preferredMileage: dataLayer?.consumerinsightsdata?.insights?.preferredMileage,
      preferredModel: dataLayer?.consumerinsightsdata?.insights?.preferredModel,
      preferredModelConfidence: dataLayer?.consumerinsightsdata?.insights?.preferredModelConfidence,
      preferredPrice: dataLayer?.consumerinsightsdata?.insights?.preferredPrice,
      preferredVehicleAgeRange: dataLayer?.consumerinsightsdata?.insights?.preferredVehicleAgeRange,
      preferredVehicleAgeRangeConfidence: dataLayer?.consumerinsightsdata?.insights?.preferredVehicleAgeRangeConfidence,
      preferredVehicleCatalogID: dataLayer?.consumerinsightsdata?.insights?.preferredVehicleCatalogID,
      preferredVehicleCatalogIDConfidence: dataLayer?.consumerinsightsdata?.insights?.preferredVehicleCatalogIDConfidence,
      preferredVehicleType: dataLayer?.consumerinsightsdata?.insights?.preferredVehicleType,
      priceSensitivity: dataLayer?.consumerinsightsdata?.insights?.priceSensitivity,
      primaryMarketDMA: dataLayer?.consumerinsightsdata?.insights?.primaryMarketDMA,
      primaryMarketZip: dataLayer?.consumerinsightsdata?.insights?.primaryMarketZip,
      recentActivityPreferredBodystyle: dataLayer?.consumerinsightsdata?.insights?.recentActivityPreferredBodystyle,
      recentActivityPreferredFuelCategory: dataLayer?.consumerinsightsdata?.insights?.recentActivityPreferredFuelCategory,
      recentActivityPreferredMake: dataLayer?.consumerinsightsdata?.insights?.recentActivityPreferredMake,
      recentActivityPreferredModel: dataLayer?.consumerinsightsdata?.insights?.recentActivityPreferredModel,
      recentActivitySecondPreferredFuelCategory: dataLayer?.consumerinsightsdata?.insights?.recentActivitySecondPreferredFuelCategory,
      recentActivitySecondPreferredMake: dataLayer?.consumerinsightsdata?.insights?.recentActivitySecondPreferredMake,
      recentActivitySecondPreferredModel: dataLayer?.consumerinsightsdata?.insights?.recentActivitySecondPreferredModel,
      secondaryMarketDMA: dataLayer?.consumerinsightsdata?.insights?.secondaryMarketDMA,
      secondaryMarketZip: dataLayer?.consumerinsightsdata?.insights?.secondaryMarketZip,
      secondPreferredFuelCategory: dataLayer?.consumerinsightsdata?.insights?.secondPreferredFuelCategory,
      secondPreferredMake: dataLayer?.consumerinsightsdata?.insights?.secondPreferredMake,
      secondPreferredModel: dataLayer?.consumerinsightsdata?.insights?.secondPreferredModel,
      drActivity: dataLayer?.consumerinsightsdata?.insights?.drActivity
    },
    consumerAdTargets: {
      consumerAdTargetIntent: dataLayer?.consumeradtargetsdata?.targetIntent,
      consumerAdTargetSubCategory: dataLayer?.consumeradtargetsdata?.targetSegments,
      consumerAdTargetTradinMake: dataLayer?.consumeradtargetsdata?.targetTradeInMake,
      consumerAdTargetTradinModel: dataLayer?.consumeradtargetsdata?.targetTradeInModel,
      consumerAdTargetInsightsAvailable: dataLayer?.consumeradtargetsdata?.targetInsightsAvailable,
      consumerAdTargetMakes: dataLayer?.consumeradtargetsdata?.targetMakes,
      consumerAdTargetModel: dataLayer?.consumeradtargetsdata?.altTargetModel,
      consumerAdTargetModels: dataLayer?.consumeradtargetsdata?.targetModels,
      consumerCampaignPreferencesAddedByRule: dataLayer?.peAddedByRule
    },
    consumerAdTargetsWithoutCAMPBias: {
      preferredModel: dataLayer?.consumeradtargetswithoutcampdata?.preferredModel,
      secondPreferredModel: dataLayer?.consumeradtargetswithoutcampdata?.secondPreferredModel,
      preferredMakes: dataLayer?.consumeradtargetswithoutcampdata?.preferredMakes,
      subCategory: dataLayer?.consumeradtargetswithoutcampdata?.subCategory,
      intent: dataLayer?.consumeradtargetswithoutcampdata?.intent,
      tradeInMake: dataLayer?.consumeradtargetswithoutcampdata?.tradeInMake,
      tradeInModel: dataLayer?.consumeradtargetswithoutcampdata?.tradeInModel
    },
    consumerMarketingSegments: {
      demographics: dataLayer?.consumermarketingsegmentsdata?.demographics,
      lifeEvents: dataLayer?.consumermarketingsegmentsdata?.lifeEvents,
      clusters: dataLayer?.consumermarketingsegmentsdata?.clusters
    }
  }, dataLayer?.googleanalytics, {
    page: null
  }, props))));
  emptyDataLayerQueue(currentWindow);
};
export const emptyDataLayerQueue = currentWindow => {
  if (currentWindow.queueddataLayerInteractions && currentWindow.queueddataLayerInteractions.forEach) {
    currentWindow.queueddataLayerInteractions.forEach(entry => {
      currentWindow.firedDataLayer?.push?.(entry);
      currentWindow.dataLayer.push(entry);
    });
    replaceInteractionQueue(currentWindow);
  }
};
const replaceInteractionQueue = currentWindow => {
  currentWindow.queueddataLayerInteractions = {
    push: entry => {
      currentWindow.firedDataLayer?.push?.(entry);
      currentWindow.dataLayer.push(entry);
    }
  };
};
const initialize = currentWindow => {
  currentWindow.dataLayer = currentWindow.dataLayer || [];
  currentWindow.firedDataLayer = currentWindow.firedDataLayer || [];
  if (currentWindow.__ANALYTICS_PROXY_CONFIG__) {
    replaceInteractionQueue(currentWindow);
  } else {
    currentWindow.queueddataLayerInteractions = [];
  }
  logEvent(currentWindow.dataLayer, 'Initial Events');
};
export default {
  click,
  impression,
  pageView,
  initialize
};